import React, { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import { cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import { NavLink } from "react-router-dom";
import { NavLinkProps } from "react-router-dom";
import useHover from "../../utils/react-use-hover";
import { FlexCol } from "../../utils/flexLayouts";

// Определение стилей для кнопки
const buttonStyle = cva(
  [
    "flex flex-row items-center button-state relative",
    "transition-colors duration-150 ease-in-out",
    "px-2.5 h-10",
    "rounded-lg",
    "text-[#637381] hover:bg-[#F3F4F6] active:text-darkblue",
  ],
  {
    variants: {
      mini: {
        true: "justify-center ",
        false: "gap-x-2 justify-start",
      },
      active: {
        true: "bg-[#F3F4F6] text-[#637381] button-state-active",
        false: "",
      },
    },
    defaultVariants: {
      mini: false,
      active: false,
    },
  }
);

// Определение типов для пропсов
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  text?: string;
  mini?: boolean;
  classIcon?: string;
}

interface LinkPropsType extends NavLinkProps {
  icon: ReactNode;
  text?: ReactNode;
  mini?: boolean;
  active?: boolean;
  tooltip?: boolean;
}

// Компонент Button
export const Button: React.FC<ButtonProps> = ({ icon, text, mini = false, className, classIcon, ...props }) => {
  return (
    <button className={twMerge(buttonStyle({ mini: mini }), className)} {...props}>
      <FlexCol className={classIcon}>{icon}</FlexCol>
      {!mini && <FlexCol className="leading-[18px]">{text}</FlexCol>}
    </button>
  );
};

export const LinkButton: React.FC<LinkPropsType> = ({
  to,
  icon,
  text = "",
  mini = false,
  active = false,
  tooltip = true,
  className,
  ...props
}) => {
  const [isHover, hoverProps] = useHover({ mouseEnterDelayMS: 1000, mouseLeaveDelayMS: 500 });
  useEffect(() => {
    return () => {
      if (hoverProps) {
        setTimeout(() => hoverProps.onMouseLeave(), 1000);
      }
    };
  }, [hoverProps]);

  return (
    <NavLink
      to={to}
      className={twMerge(buttonStyle({ mini: mini, active: active }), className as string)}
      {...(hoverProps || {})}
      {...props}
    >
      <FlexCol className="link-icon">{icon}</FlexCol>
      {!mini && <FlexCol className="leading-[18px]">{text}</FlexCol>}
      {mini && isHover && !active && (
        <FlexCol className="absolute left-[calc(100%+20px)] top-0 bottom-0 text-sm justify-center">
          <div className="triangle-left flex py-1 px-3 rounded-md bg-white shadow-custom leading-[18px]">{text}</div>
        </FlexCol>
      )}
    </NavLink>
  );
};

export const TooltipWithDelay: React.FC<LinkPropsType> = ({
  to,
  icon,
  text = "",
  mini = false,
  active = false,
  tooltip = true,
  className,
  ...props
}) => {
  const [isHover, hoverProps] = useHover({ mouseEnterDelayMS: 1000, mouseLeaveDelayMS: 500 });

  useEffect(() => {
    return () => {
      if (hoverProps) {
        setTimeout(() => hoverProps.onMouseLeave(), 1000);
      }
    };
  }, [hoverProps]);

  return (
    <NavLink
      to={to}
      className={twMerge(buttonStyle({ mini: mini, active: active }), className as string)}
      {...(hoverProps || {})}
      {...props}
    >
      <FlexCol className="link-icon">{icon}</FlexCol>
      {!mini && <FlexCol className="leading-[18px]">{text}</FlexCol>}
      {mini && isHover && !active && (
        <FlexCol className="absolute left-[calc(100%+20px)] top-0 bottom-0 text-sm justify-center">
          <div className="triangle-left flex py-1 px-3 rounded-md bg-white shadow-custom leading-[18px]">{text}</div>
        </FlexCol>
      )}
    </NavLink>
  );
};
